import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import './PrivacyPolicy.css';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="privacy-policy">
      <PublicNavbar>
        <Link to="/terms-and-conditions">Terms</Link>
        <Link to="/refund-policy">Refunds</Link>
        <Link to="/customer/login" className="nav-btn nav-btn-customer">Promoter Login</Link>
        <Link to="/admin/login" className="nav-btn nav-btn-admin">Admin Login</Link>
      </PublicNavbar>

      <main className="policy-content">
        <div className="container">
          <h1>Terms &amp; Conditions &ndash; TappyTray</h1>
          <p className="last-updated"><strong>Last Updated: 15 March 2026</strong></p>

          <p>
            Welcome to TappyTray, a digital platform operated by BoilingCode LLP ("Company", "we", "our", or "us").
          </p>
          <p>
            These Terms and Conditions govern your access to and use of the TappyTray mobile application, website, and related services (collectively referred to as the "Platform").
          </p>
          <p>
            By registering, accessing, or using the Platform, you agree to comply with these Terms.
          </p>

          <section>
            <h2>1. About the Platform</h2>
            <p>
              TappyTray is a community-based digital ecosystem designed to simplify services and communication within residential societies.
            </p>
            <p>The Platform enables:</p>
            <ul>
              <li>Residents to connect with their society</li>
              <li>Society committees to manage community operations</li>
              <li>Local businesses to offer products and services to residents</li>
            </ul>
            <p>TappyTray acts as a technology platform facilitating interactions between residents, societies, and businesses.</p>
          </section>

          <section>
            <h2>2. Eligibility</h2>
            <p>To use the Platform:</p>
            <ul>
              <li>You must be at least 18 years of age.</li>
              <li>You must provide accurate and complete information during registration.</li>
              <li>You must belong to or be associated with a residential society using TappyTray.</li>
            </ul>
            <p>The Company reserves the right to suspend or terminate accounts that provide false or misleading information.</p>
          </section>

          <section>
            <h2>3. User Accounts</h2>
            <p>Users may create an account by providing:</p>
            <ul>
              <li>Mobile number</li>
              <li>Email address (if applicable)</li>
              <li>Society and flat information</li>
            </ul>
            <p>You are responsible for maintaining the confidentiality of your login credentials.</p>
            <p>Any activity performed through your account will be considered your responsibility.</p>
          </section>

          <section>
            <h2>4. Society Management Features</h2>
            <p>Society administrators or committee members may use the Platform to:</p>
            <ul>
              <li>Manage resident records</li>
              <li>Create maintenance bills</li>
              <li>Post announcements and notices</li>
              <li>Manage society services</li>
            </ul>
            <p>TappyTray provides the technology infrastructure but does not participate in society governance or decision-making.</p>
          </section>

          <section>
            <h2>5. Payments</h2>
            <p>Residents may use the Platform to make payments such as:</p>
            <ul>
              <li>Society maintenance fees</li>
              <li>Service bookings</li>
              <li>Purchases from local businesses</li>
            </ul>
            <p>Payments are processed through secure third-party payment gateways.</p>
            <p>TappyTray does not store credit card, debit card, or banking credentials.</p>
            <p>The Company shall not be responsible for failures or delays caused by payment gateway providers or banking systems.</p>
          </section>

          <section>
            <h2>6. Vendors and Service Providers</h2>
            <p>The Platform may allow third-party service providers such as:</p>
            <ul>
              <li>Laundry services</li>
              <li>Electricians</li>
              <li>Plumbers</li>
              <li>Local shops and restaurants</li>
            </ul>
            <p>These vendors operate independently and are solely responsible for the quality and delivery of their services.</p>
            <p>TappyTray shall not be liable for disputes between users and vendors.</p>
          </section>

          <section>
            <h2>7. Promotions and Advertising</h2>
            <p>Businesses may promote products or services within the Platform.</p>
            <p>All promotional content must:</p>
            <ul>
              <li>Be accurate and lawful</li>
              <li>Not contain misleading claims</li>
              <li>Comply with applicable laws</li>
            </ul>
            <p>The Company reserves the right to remove any promotional content that violates these guidelines.</p>
          </section>

          <section>
            <h2>8. User Conduct</h2>
            <p>Users agree not to:</p>
            <ul>
              <li>Post abusive, defamatory, or offensive content</li>
              <li>Misuse personal data of other residents</li>
              <li>Attempt to hack or disrupt the platform</li>
              <li>Use the platform for unlawful activities</li>
              <li>Impersonate another individual or entity</li>
            </ul>
            <p>Violation of these rules may result in suspension or termination of the user account.</p>
          </section>

          <section>
            <h2>9. Intellectual Property</h2>
            <p>All content and materials on the Platform including software, logo, branding, design, and features are owned by BoilingCode LLP and protected under applicable intellectual property laws.</p>
            <p>Users may not copy, distribute, or reproduce platform content without prior written permission.</p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>TappyTray functions as a technology platform connecting residents, societies, and vendors.</p>
            <p>The Company shall not be liable for:</p>
            <ul>
              <li>Disputes between residents and society management</li>
              <li>Vendor service quality or service delays</li>
              <li>Losses caused by third-party service providers</li>
              <li>Indirect or consequential damages arising from platform use</li>
            </ul>
          </section>

          <section>
            <h2>11. Account Suspension or Termination</h2>
            <p>The Company reserves the right to suspend or terminate accounts that:</p>
            <ul>
              <li>Violate these Terms</li>
              <li>Engage in fraudulent or abusive activity</li>
              <li>Misuse the platform</li>
            </ul>
            <p>Users may request deletion of their account by contacting the support team or using our <Link to="/delete-account">account deletion request form</Link>.</p>
          </section>

          <section>
            <h2>12. Changes to Terms</h2>
            <p>We may update these Terms and Conditions from time to time.</p>
            <p>Users will be notified of significant updates through the Platform or via email.</p>
            <p>Continued use of the Platform after changes indicates acceptance of the updated Terms.</p>
          </section>

          <section>
            <h2>13. Governing Law</h2>
            <p>These Terms shall be governed by and interpreted in accordance with the laws of India.</p>
            <p>Any disputes arising from these Terms shall be subject to the jurisdiction of courts located in Rajasthan, India.</p>
          </section>

          <section>
            <h2>14. Contact Information</h2>
            <div className="contact-info">
              <p>If you have any questions regarding these Terms and Conditions, please contact:</p>
              <p><strong>BoilingCode LLP</strong><br />Operator of TappyTray</p>
              <p><strong>Email</strong>: <a href="mailto:yogita@boilingcode.com">yogita@boilingcode.com</a></p>
              <p><strong>Website</strong>: <a href="https://tappytray.com">www.tappytray.com</a></p>
            </div>
          </section>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 BoilingCode LLP. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
            <Link to="/refund-policy">Refund Policy</Link>
            <Link to="/delete-account">Delete Account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
